import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { SERVICES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { first_name: "", last_name: "", address: "", email: "", phone: "", service: "", message: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.address || !form.email) {
      toast.error("Please fill in your name, address, and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Quote request sent! We'll be in touch soon.");
    } catch (err) {
      const msg = err?.response?.status === 422 ? "Please enter a valid email address." : "Something went wrong. Please call us at 720-449-1197.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-12 rounded-xl border border-white/15 bg-white/5 px-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition";

  return (
    <section id="quote" className="py-24 bg-ink">
      <div className="max-w-5xl mx-auto px-6">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-8 md:p-12 shadow-2xl">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-2">
              <p className="uppercase tracking-[0.2em] text-sm font-semibold text-brand">Free estimate</p>
              <h2 className="mt-3 font-display font-bold text-4xl tracking-tight text-white">
                Get your quote
              </h2>
              <p className="mt-4 text-slate-400 leading-relaxed">
                Tell us where you are and how to reach you. We'll send a no-obligation quote fast — usually the same day.
              </p>
              <ul className="mt-6 space-y-3 text-slate-300 text-sm">
                {["No pressure, no obligation", "Fast, same-day responses", "Serving Castle Rock, Larkspur & Parker"].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <CheckCircle2 size={18} className="text-brand" strokeWidth={1.75} /> {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-3">
              {done ? (
                <div data-testid="quote-success" className="rounded-2xl bg-brand/10 border border-brand/30 p-8 text-center">
                  <CheckCircle2 className="mx-auto text-brand" size={48} strokeWidth={1.5} />
                  <h3 className="mt-4 font-display font-bold text-2xl text-white">Request received!</h3>
                  <p className="mt-2 text-slate-300">Thanks for reaching out. A ClearCo team member will contact you shortly with your free quote.</p>
                  <button
                    data-testid="quote-reset-button"
                    onClick={() => setDone(false)}
                    className="mt-6 rounded-xl border border-white/20 text-white px-6 py-3 font-semibold hover:bg-white/10 transition"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="quote-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="quote-first-name-input" className={inputCls} placeholder="First name *" value={form.first_name} onChange={update("first_name")} />
                    <input data-testid="quote-last-name-input" className={inputCls} placeholder="Last name *" value={form.last_name} onChange={update("last_name")} />
                  </div>
                  <input data-testid="quote-address-input" className={inputCls} placeholder="Home address *" value={form.address} onChange={update("address")} />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="quote-email-input" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={update("email")} />
                    <input data-testid="quote-phone-input" className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} />
                  </div>
                  <select data-testid="quote-service-select" className={`${inputCls} appearance-none`} value={form.service} onChange={update("service")}>
                    <option value="" className="bg-ink">Service needed (optional)</option>
                    {SERVICES.map((s) => (
                      <option key={s.key} value={s.title} className="bg-ink">{s.title}</option>
                    ))}
                    <option value="Multiple services" className="bg-ink">Multiple services</option>
                  </select>
                  <textarea data-testid="quote-message-input" rows={3} className={`${inputCls} h-auto py-3 resize-none`} placeholder="Anything else we should know? (optional)" value={form.message} onChange={update("message")} />
                  <button
                    data-testid="quote-submit-button"
                    type="submit"
                    disabled={loading}
                    className="w-full h-13 py-4 rounded-xl bg-brand hover:bg-brand-hover text-white font-semibold text-base transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-brand/30 disabled:opacity-60 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                  >
                    {loading ? (<><Loader2 className="animate-spin" size={18} /> Sending...</>) : "Request My Free Quote"}
                  </button>
                  <p className="text-xs text-slate-500 text-center">By submitting, you agree to be contacted about your quote.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
